import { Paragraph, Table, Toast, useToast } from '@zillow/constellation-10';
import {
  SatelliteRenterProfileCompletenessType,
  type SatelliteWebLandlordGetLatestForContactsPageConversationDto,
} from '@zg-rentals/ts-satellite-client';
import ActionMenuTableCell from '../ActionMenuTableCell';
import ProspectStageTableCell from '../ProspectStageTableCell';
import NotesTableCell from '../NotesTableCell';
import LatestContactTableCell from '../LatestContactTableCell';
import RenterProfileTableCell from '../RenterProfileTableCell';
import ContactInfoTableCell from '../ContactInfoTableCell';
import NameTableCell from '../NameTableCell';
import type { ModalType } from '../../../../types/ModalType';
import type { OpenModal } from '../..';

export const NOT_PROVIDED = 'Not provided';

/**
 *
 * @description Helper function to determine if (Phone lead) should be displayed.
 * We should always try to render the lead's name if it exists, as we have caller id for phone leads
 */
const getLeadDisplayName = (isPhoneLead: boolean) => {
  if (isPhoneLead) {
    return '(Phone lead)';
  }

  return NOT_PROVIDED;
};

export type HandleOpenModal = (modalType: ModalType) => void;

type TableContentProps = {
  conversations: Array<SatelliteWebLandlordGetLatestForContactsPageConversationDto>;
  openModal: OpenModal;
};

const MISSING_LISTING_TOAST_MESSAGE = 'Listing couldn’t be found';

export default function TableContent({ conversations, openModal }: TableContentProps) {
  const { enqueueToast } = useToast();

  return (
    <Table.Body>
      {conversations.map((conversation) => {
        const {
          conversationId,
          renterName,
          renterEmail,
          renterPhone,
          isPhoneLead,
          listingDetails,
          mostRecentMessage,
          note,
          renterProfile,
          hasInquiry,
        } = conversation;
        const { hasSentApplication, hasScheduledTour } = note;

        const leadNameDisplay = renterName || getLeadDisplayName(isPhoneLead);
        const hasNotes = !!note.note || hasScheduledTour || hasSentApplication;
        const moveInDate = renterProfile?.attributes?.moveInDate;
        const listingAlias = listingDetails?.listingAlias;

        const handleOpenModal: HandleOpenModal = (modalType: ModalType) => {
          if (!listingAlias) {
            enqueueToast(<Toast tone="critical">{MISSING_LISTING_TOAST_MESSAGE}</Toast>);
            return;
          }
          return openModal({
            listingAlias,
            conversationId,
            modalType,
            conversation, // Pass the entire conversation object
            renterName,
            renterProfile,
          });
        };

        return (
          <Table.Row key={conversationId}>
            <NameTableCell intentLabel={renterProfile?.leadIntentValue} name={leadNameDisplay} />

            <ContactInfoTableCell email={renterEmail || ''} phone={renterPhone || ''} />

            <Table.Cell css={{ verticalAlign: 'top' }}>
              {moveInDate || (
                <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                  {NOT_PROVIDED}
                </Paragraph>
              )}
            </Table.Cell>

            <RenterProfileTableCell
              isRenterProfileHidden={renterProfile?.isRenterProfileHidden || false}
              onRenterProfileClick={handleOpenModal}
              renterProfileCompleteness={
                renterProfile?.renterProfileCompleteness || SatelliteRenterProfileCompletenessType.NOT_AVAILABLE
              }
            />

            <LatestContactTableCell
              isPhoneLead={isPhoneLead}
              latestMessage={mostRecentMessage?.message || ''}
              messageDateMs={mostRecentMessage?.messageDateMs}
              onMessageClick={handleOpenModal}
              senderName={mostRecentMessage?.senderName || ''}
              listingAlias={listingAlias ?? ''}
              conversationId={conversationId}
            />

            <NotesTableCell
              hasNotes={hasNotes}
              lastUpdatedMs={note?.noteLastUpdatedMs}
              onAddNotesClick={handleOpenModal}
            />

            <ProspectStageTableCell
              hasInquiry={hasInquiry}
              hasScheduledTour={hasScheduledTour}
              hasSentApplication={hasSentApplication}
            />

            <ActionMenuTableCell
              isPhoneLead={isPhoneLead}
              onActionClick={handleOpenModal}
              conversation={conversation}
            />
          </Table.Row>
        );
      })}
    </Table.Body>
  );
}
